import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { getLocalisedContent } from '@/lib/getLocalisedContent';
import { useChildren } from './useChildren';
import { ageInMonths } from './useSparkQuestion';
import { useVisibilityRefetch } from './useVisibilityRefetch';

export type ContentType =
  | 'memory_prompt'
  | 'spark_question'
  | 'milestone'
  | 'activity'
  | 'tip'
  | 'occasion_prompt'
  | 'game';

export interface ContentLibraryItem {
  id: string;
  type: ContentType;
  title: string | null;
  body: string;
  age_min_months: number | null;
  age_max_months: number | null;
  genders: string[];
  seasons: string[];
  occasions: string[];
  capture_type: 'voice' | 'drawing' | 'photo' | 'written' | 'video' | null;
  voice_required: boolean;
  tags: string[];
  frequency: 'always' | 'daily' | 'weekly' | 'occasional' | 'rare';
  is_dismissable: boolean;
  priority_days: number;
  is_onboarding: boolean;
  is_seasonal: boolean;
  card_type: 'challenge' | 'tip' | 'memory' | 'motivational' | 'upsell';
  /** Two-axis subject model (added 2026-04-30). Use these instead of card_target + child_specific. */
  subject: 'parent_only' | 'any_child' | 'specific_child';
  child_assignment: 'expand' | 'user_selects';
  viewer: 'anyone' | 'co_parent';
  /** @deprecated — use subject + child_assignment */
  child_specific: boolean;
  requires_child: boolean;
  weight: number;
  active: boolean;
  source: string;
  instructions: string | null;
  tip: string | null;
  /** @deprecated — use subject + child_assignment. Who this card targets: 'child' | 'parent' | 'tribe' | 'any' | 'from_child' */
  card_target: 'child' | 'parent' | 'tribe' | 'any' | 'from_child';
  translations?: Record<string, { title?: string | null; body?: string | null; instructions?: string | null; tip?: string | null }> | null;
  /** Resolved at feed-assembly time — not stored in DB. Set on child-card copies. */
  _resolvedChildId?: string;
  _resolvedChildName?: string;
  /** Unique feed key: `${id}:${child_id}` for child cards, `${id}` for others. */
  _feedKey?: string;
  /** Collection journey context — set at feed-assembly time for step cards. Not stored in DB. */
  _collectionContext?: {
    templateId: string;
    templateName: string;
    stepOrder: number;
    stepCount: number;
    stepTitleOverride: string | null;
  };
}

/**
 * @deprecated — replaced by contentInteractionSubscriber (src/lib/contentInteractionSubscriber.ts)
 * which listens to card.* events fired by useFeedEngine and writes to content_interactions.
 * No longer called anywhere. Safe to remove in next cleanup.
 */
export async function trackInteraction(
  userId: string,
  contentId: string,
  interactionType: 'shown' | 'yes_tapped' | 'dismissed' | 'swiped_past',
  sessionId: string,
): Promise<void> {
  try {
    await supabase.from('content_interactions').insert({
      user_id: userId,
      content_id: contentId,
      interaction_type: interactionType,
      session_id: sessionId,
    });
  } catch {
    // fire-and-forget — never block UI
  }
}

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';

export function getCurrentSeason(date: Date = new Date()): Season {
  const m = date.getMonth(); // 0 = Jan
  if (m >= 2 && m <= 4) return 'spring';   // Mar-May
  if (m >= 5 && m <= 7) return 'summer';   // Jun-Aug
  if (m >= 8 && m <= 10) return 'autumn';  // Sep-Nov
  return 'winter';                          // Dec-Feb
}

interface UseContentLibraryOptions {
  type?: ContentType | ContentType[];
  limit?: number;
  /** When true, restrict to rows whose seasons match the current season (or are empty). Defaults to true. */
  season?: boolean;
}

interface UseContentLibraryResult {
  items: ContentLibraryItem[];
  isLoading: boolean;
  refresh: () => void;
}

/**
 * Pulls contextually relevant entries from `content_library` for the active
 * child. Filters by age range, gender and (optionally) the current season,
 * then replaces every `[name]` placeholder in body and title with the child's
 * name.
 *
 * The library is small (a few hundred rows) so we let the server filter by
 * `active` and `type`, and finish the array/null filtering in JS where the
 * semantics are clearer than chained PostgREST `.or()` calls.
 */
export function useContentLibrary(
  options: UseContentLibraryOptions = {},
): UseContentLibraryResult {
  const { type, limit = 3, season = true } = options;
  const { activeChild, children } = useChildren();
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const [items, setItems] = useState<ContentLibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // Refetch on tab focus so newly-seeded library rows show up. The shuffle
  // is intentional — same useEffect path as a manual refresh().
  useVisibilityRefetch(refresh);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);

      const ageMonths = ageInMonths(activeChild?.birthdate);
      const childGender = activeChild?.gender ?? 'neutral';
      const currentSeason = getCurrentSeason();
      const hasChildren = children.length > 0;

      let query = supabase
        .from('content_library')
        .select('*')
        .eq('active', true);

      if (type) {
        const types = Array.isArray(type) ? type : [type];
        query = query.in('type', types);
      }

      const { data, error } = await query;

      if (cancelled) return;

      if (error) {
        console.error('useContentLibrary:', error.message);
        setItems([]);
        setIsLoading(false);
        return;
      }

      const rows = (data as unknown as ContentLibraryItem[]) ?? [];

      // Client-side filtering — clearer than chained PostgREST .or() calls.
      const matches = rows.filter((row) => {
        // Requires a child to be meaningful — hide when user has none.
        if (row.requires_child && !hasChildren) return false;

        // Age window: NULL bound = unbounded.
        if (row.age_min_months != null && ageMonths < row.age_min_months) return false;
        if (row.age_max_months != null && ageMonths > row.age_max_months) return false;

        // Gender: empty array = any gender; otherwise must include the
        // child's gender or 'neutral'.
        if (row.genders && row.genders.length > 0) {
          if (!row.genders.includes(childGender) && !row.genders.includes('neutral')) {
            return false;
          }
        }

        // Season: empty array = any season; only enforced when caller asked.
        if (season && row.seasons && row.seasons.length > 0) {
          if (!row.seasons.includes(currentSeason)) return false;
        }

        return true;
      });

      // Locale filter: apply translation overlay; drop rows with no translation for non-EN locales.
      const localised = matches
        .map((row) => getLocalisedContent(row, locale, false))
        .filter((row): row is ContentLibraryItem => row !== null);

      // Random sample down to `limit`.
      const shuffled = [...localised].sort(() => Math.random() - 0.5).slice(0, limit);

      const childName = activeChild?.name ?? 'your child';
      const personalised = shuffled.map((item) => ({
        ...item,
        title: item.title ? item.title.replace(/\[name\]/g, childName) : null,
        body: item.body.replace(/\[name\]/g, childName),
      }));

      setItems(personalised);
      setIsLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    activeChild?.id,
    activeChild?.birthdate,
    activeChild?.gender,
    activeChild?.name,
    children.length,
    type,
    limit,
    season,
    locale,
    tick,
  ]);

  return { items, isLoading, refresh };
}
